/* eslint-disable id-length */
import classNames from "classnames";
import debounce from "lodash/debounce";
import { useCallback, useRef, useState } from "react";
import BuildAnnotations from "~/components/build/Show/components/annotations/BuildAnnotations";
import Count from "~/components/build/Show/components/Count";
import JobAnnotations from "~/components/build/Show/components/annotations/JobAnnotations";
import { ResizeHandle } from "~/components/build/Show/components/ResizeHandle";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import {
  clampAnnotationsPanelWidth,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import { AnnotationsTab } from "~/components/build/Show/lib/types";

const RESIZE_DEBOUNCE_WAIT = 150;
const DEFAULT_TAB = AnnotationsTab.Build;

const TAB_LABELS: Record<AnnotationsTab, string> = {
  [AnnotationsTab.Build]: "Build",
  [AnnotationsTab.Jobs]: "Jobs",
};

function AnnotationsPanelTab({
  tab,
  isActive,
  count,
  onSelect,
}: {
  tab: AnnotationsTab;
  isActive: boolean;
  count: number;
  onSelect: (tab: AnnotationsTab) => void;
}) {
  const handleClick = useCallback(() => onSelect(tab), [onSelect, tab]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={classNames(
        "flex items-center gap-1 self-center rounded-md border border-gray-500 px-2 py-1 text-sm transition-colors duration-75",
        "text-charcoal-400 hover:border-slate-400 hover:bg-white hover:text-current",
        {
          "text-charcoal-700 shadow-xs border-slate-500 bg-white": isActive,
        },
      )}
    >
      {TAB_LABELS[tab]}
      {count > 0 && <Count>{count}</Count>}
    </button>
  );
}

function AnnotationsPanelContent({ activeTab }: { activeTab: AnnotationsTab }) {
  switch (activeTab) {
    case AnnotationsTab.Build:
      return <BuildAnnotations />;
    case AnnotationsTab.Jobs:
      return <JobAnnotations />;
  }
}

export default function AnnotationsPanel() {
  const { build } = useBuild();
  const annotationCounts = getAnnotationCounts(build);
  const [activeTab, setActiveTab] = useState<AnnotationsTab>(DEFAULT_TAB);

  const width = useBuildPreferencesStore((state) => state.annotationsPanelWidth);
  const setAnnotationsPanelWidth = useBuildPreferencesStore(
    (state) => state.setAnnotationsPanelWidth,
  );

  const panelRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const persistWidth = useCallback(
    debounce((value: number) => setAnnotationsPanelWidth(value), RESIZE_DEBOUNCE_WAIT),
    [],
  );

  const resize = useCallback(
    (x: number) => {
      if (!panelRef.current) {
        return;
      }
      const rect = panelRef.current.getBoundingClientRect();
      const nextWidth = clampAnnotationsPanelWidth(rect.right - x);
      panelRef.current.style.width = `${nextWidth}px`;
      persistWidth(nextWidth);
    },
    [persistWidth],
  );

  return (
    <div
      ref={panelRef}
      data-testid="AnnotationsPanel"
      style={{ width }}
      className="relative flex h-full min-w-0 flex-auto flex-col rounded-md border border-gray-500 bg-white md:max-w-[50vw] md:flex-none"
    >
      <ResizeHandle direction="horizontal" onResize={resize} className="left-[1px] rounded-l-md" />

      <div className="flex flex-auto flex-col">
        <div className="flex gap-1.5 border-b border-gray-400 p-1.5">
          {Object.values(AnnotationsTab).map((tab) => (
            <AnnotationsPanelTab
              key={tab}
              tab={tab}
              isActive={tab === activeTab}
              count={annotationCounts[tab]}
              onSelect={setActiveTab}
            />
          ))}
        </div>

        <div className="relative flex-auto">
          <div className="absolute flex h-full w-full overflow-y-auto">
            <div className="relative flex w-full flex-1 flex-col gap-2 p-3">
              <AnnotationsPanelContent activeTab={activeTab} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
